import React, {memo} from "react";
import {Button} from "@/components/ui/Button";
import {CircleCheck, CloudOff, Loader, RefreshCw} from "lucide-react";
import {useSave} from "@/contexts/SaveContext";
import {SavingState} from "@/types/node";
import {AnimatePresence, motion} from "framer-motion";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {cn} from "@/lib/utils";

const AutoSave: React.FC<{disabled?: boolean}> = ({disabled = false}) => {
  const {handleSave, savingState} = useSave();

  const animationProps = {
    initial: {opacity: 0, y: 10},
    animate: {opacity: 1, y: 0},
    exit: {opacity: 0, y: -10},
    transition: {duration: 0.07, ease: "easeOut"},
  };

  return (
    <motion.div
      className="flex items-center justify-center text-sm font-medium w-28"
      {...animationProps}
    >
      <AnimatePresence mode="wait">
        {savingState === SavingState.SAVED && (
          <motion.div
            className={cn(
              "flex items-center gap-1 text-green-dark px-2 py-1.5 rounded-md",
              disabled && "text-disabled",
            )}
            key="saved"
            {...animationProps}
          >
            <CircleCheck size={16} />
            All Saved
          </motion.div>
        )}
        {savingState === SavingState.SAVING && (
          <motion.div
            className="flex items-center gap-1 text-label px-2 py-1.5 rounded-md"
            key="saving"
            {...animationProps}
          >
            <Loader size={16} className="animate-spin" />
            Saving...
          </motion.div>
        )}
        {savingState === SavingState.LOADING && (
          <motion.div
            className="flex items-center gap-1 text-label px-2 py-1.5 rounded-md"
            key="saving"
            {...animationProps}
          >
            <Loader size={16} className="animate-spin" />
            Loading...
          </motion.div>
        )}
        {(savingState === SavingState.ERROR ||
          savingState === SavingState.ERROR_ACTIONS ||
          savingState === SavingState.ERROR_LOADING) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.div
                className="flex items-center justify-center w-full gap-1 text-orange-dark hover:border hover:border-orange-600/20 bg-orange-600/15 cursor-pointer px-2 py-1.5 rounded-md"
                key="error"
                {...animationProps}
                onClick={() => {
                  if (savingState === SavingState.ERROR_LOADING) {
                    window.location.reload();
                  } else {
                    handleSave();
                  }
                }}
              >
                <CloudOff size={16} />
                {savingState === SavingState.ERROR_LOADING && "Load Error"}
                {savingState === SavingState.ERROR && "Unsaved"}
                {savingState === SavingState.ERROR_ACTIONS && "Error"}
              </motion.div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="end"
              className="rounded-md p-3 w-[210px] border-0"
            >
              <TooltipArrow className="fill-slate-800" />
              <div className="flex items-center flex-col gap-3 text-sm font-medium">
                {savingState === SavingState.ERROR_LOADING && (
                  <>
                    <p className="text-white">
                      Failed to load your flow data. Please refresh the page to
                      try again.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-[#161618] font-medium"
                      onClick={() => window.location.reload()}
                    >
                      <RefreshCw size={16} />
                      Refresh Page
                    </Button>
                  </>
                )}
                {savingState === SavingState.ERROR && (
                  <>
                    <p className="text-white">
                      You have unsaved changes. Do not close the page to avoid
                      losing all progress.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-[#161618] font-medium"
                      onClick={() => {
                        if (savingState === SavingState.ERROR) {
                          handleSave();
                        }
                      }}
                    >
                      <RefreshCw size={16} />
                      Try Again
                    </Button>
                  </>
                )}
                {savingState === SavingState.ERROR_ACTIONS && (
                  <p className="text-white">
                    There was an error attaching actions.
                    <br />
                    Please try publishing again.
                  </p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default memo(AutoSave);
